import { Link } from '@tanstack/react-router';
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { arrivalShort, clockTime } from './taskLabels';

/** „Jetzt dran“: die eigene laufende Aufgabe mit Fotoring, Startzeit und dem Weg zurück hinein. */
export function NowCard({
  task,
  today,
  timeZone,
  failure,
}: {
  task: CleaningTaskQueueResponse;
  today: string;
  timeZone?: string;
  /** Ein Schritt an dieser Aufgabe scheiterte, nachdem sie schon lief (etwa der Abschluss). */
  failure: string | null;
}) {
  const total = task.photoSlotCount;
  const filled = task.filledPhotoSlotCount;
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  const facts = [
    arrivalShort(task, today),
    task.hasOpenIssue ? 'Meldung offen' : null,
    total === 0 ? 'ohne Fotos' : null,
  ].filter(Boolean);
  return (
    <article
      aria-label={`Jetzt dran: ${task.propertyName}`}
      className="bg-ink rounded-tile flex items-center gap-3.5 p-4 text-white"
    >
      {total > 0 && (
        <span
          aria-hidden
          className="grid size-[74px] flex-none place-items-center rounded-full"
          style={{
            background: `conic-gradient(var(--color-mint) 0 ${pct}%, rgb(255 255 255 / 0.15) ${pct}% 100%)`,
          }}
        >
          <span className="bg-ink grid size-[58px] place-items-center rounded-full font-serif text-lg font-bold tabular-nums">
            {filled}/{total}
          </span>
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-mint font-mono text-[10px] font-semibold uppercase tracking-[0.14em]">
          Jetzt dran
        </span>
        <b className="font-serif text-[22px] leading-tight">
          {task.propertyName}
          {task.startedAt && (
            <span className="font-sans text-sm font-semibold text-white/80">
              {' '}
              · läuft seit {clockTime(task.startedAt, timeZone)}
            </span>
          )}
        </b>
        {facts.length > 0 && <span className="text-xs text-white/75">{facts.join(' · ')}</span>}
        {total > 0 && (
          <span className="sr-only">
            {filled} von {total} Fotos
          </span>
        )}
        {failure && (
          <span role="alert" className="text-sealwax-on-dark text-sm font-semibold">
            {failure}
          </span>
        )}
      </span>
      <Link
        to="/cleaning/$id"
        params={{ id: task.id }}
        className="rounded-pill bg-mint text-forest-deep flex h-12 flex-none items-center px-4 text-sm font-bold"
      >
        Weiter
      </Link>
    </article>
  );
}
